import React from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { trackAdsEvent } from '@/lib/adsTracking';

const enquirySchema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(100),
  phone: z.string().trim().min(1, 'Enter your mobile number').max(50)
    .refine(value => /^(?:\+?[\d])[\d\s()-]{6,24}$/.test(value), 'Enter a valid mobile number'),
  email: z.string().trim().email('Enter a valid email address').max(254),
  preferredTimes: z.string().trim().max(500),
});

interface Props { hours?: number; transmission?: 'manual' | 'automatic' }

export default function IntensiveCourseEnquiry({ hours, transmission }: Props) {
  const nameRef = React.useRef<HTMLInputElement>(null);
  const courseRef = React.useRef<HTMLSelectElement>(null);
  const [selectedHours, setSelectedHours] = React.useState(hours ? String(hours) : '');
  const [selectedTransmission, setSelectedTransmission] = React.useState(transmission || '');
  const sendingRef = React.useRef(false);
  const [values, setValues] = React.useState({ name: '', phone: '', email: '', preferredTimes: '' });
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  React.useEffect(() => {
    setSelectedHours(hours ? String(hours) : '');
    setSelectedTransmission(transmission || '');
    setSuccess(false);
    setError('');
    if (hours && transmission) nameRef.current?.focus({ preventScroll: true });
    else courseRef.current?.focus({ preventScroll: true });
  }, [hours, transmission]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (sendingRef.current) return;
    if (!['5', '10', '15', '25', '35'].includes(selectedHours) || !['manual', 'automatic'].includes(selectedTransmission)) {
      setError('Choose your course hours and transmission');
      return;
    }
    const parsed = enquirySchema.safeParse(values);
    if (!parsed.success) { setError(parsed.error.issues[0]?.message || 'Check your details'); return; }
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      const { data, error: sendError } = await supabase.functions.invoke('send-contact-inquiry', {
        body: { ...parsed.data, course: `${selectedHours} Hour Intensive Block — ${selectedTransmission}`, inquiryType: 'lesson' },
      });
      if (sendError || data?.success !== true) throw new Error('Enquiry not confirmed');
      trackAdsEvent('course_enquiry_success');
      setSuccess(true);
      setValues({ name: '', phone: '', email: '', preferredTimes: '' });
    } catch {
      setError('Your enquiry could not be sent. Please try again or text Ian directly.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  return (
    <div id="course-enquiry" className="mt-12 border-t border-border pt-8 scroll-mt-24 max-w-2xl mx-auto text-foreground">
      <h3 id="course-enquiry-title" className="text-2xl font-semibold mb-2">Enquire about your course</h3>
      <p className="mb-6 capitalize">{selectedHours && selectedTransmission ? `${selectedHours} hours · ${selectedTransmission}` : 'Check availability with Ian'}</p>
      {success ? <p role="status">Enquiry sent. Ian will contact you about availability.</p> : (
        <form onSubmit={submit} aria-labelledby="course-enquiry-title" aria-busy={sending} className="grid sm:grid-cols-2 gap-4">
          <fieldset disabled={sending} className="contents">
            <div className="space-y-2">
              <Label htmlFor="ice-course">Course hours</Label>
              <select ref={courseRef} id="ice-course" required aria-required="true" value={selectedHours} onChange={e => setSelectedHours(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <option value="" disabled>Choose course hours</option>
                {[5, 10, 15, 25, 35].map(h => <option key={h} value={String(h)}>{h} hours</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ice-transmission">Transmission</Label>
              <select id="ice-transmission" required aria-required="true" value={selectedTransmission} onChange={e => setSelectedTransmission(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <option value="" disabled>Choose transmission</option>
                <option value="manual">Manual</option>
                <option value="automatic">Automatic</option>
              </select>
            </div>
            <div className="space-y-2"><Label htmlFor="ice-name">Name</Label><Input ref={nameRef} id="ice-name" autoComplete="name" required maxLength={100} value={values.name} onChange={e => setValues({ ...values, name: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="ice-phone">Mobile number</Label><Input id="ice-phone" type="tel" autoComplete="tel" required maxLength={50} value={values.phone} onChange={e => setValues({ ...values, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="ice-email">Email</Label><Input id="ice-email" type="email" autoComplete="email" required maxLength={254} value={values.email} onChange={e => setValues({ ...values, email: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="ice-timing">Preferred start timing (optional)</Label><Input id="ice-timing" maxLength={500} placeholder="e.g. next month" value={values.preferredTimes} onChange={e => setValues({ ...values, preferredTimes: e.target.value })} /></div>
          </fieldset>
          {error && <p role="alert" className="sm:col-span-2 text-destructive">{error}</p>}
          <Button type="submit" disabled={sending} className="sm:col-span-2 btn-premium">{sending ? 'Sending…' : 'Send enquiry'}</Button>
        </form>
      )}
    </div>
  );
}